export interface RealtimeToolContext {
  endSession: () => void;
  [key: string]: unknown;
}

export interface RealtimeFunctionCall {
  id?: string;
  name: string;
  args?: unknown;
  arguments?: unknown;
}

export interface RealtimeToolExecutionResult {
  response: Record<string, unknown>;
  afterResponse?: () => void;
  /** If true, don't send a response back to the model for this tool call */
  skipResponse?: boolean;
}

export interface RealtimeToolDefinition {
  name: string;
  description: string;
  parameters?: Record<string, unknown>;
  /** Set to 'NON_BLOCKING' for async functions that shouldn't pause the conversation */
  behavior?: 'NON_BLOCKING';
  handler: (args: unknown, context: RealtimeToolContext) => Promise<RealtimeToolExecutionResult> | RealtimeToolExecutionResult;
}

function normalizeArgs(call: RealtimeFunctionCall): unknown {
  const raw = call.args ?? call.arguments;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw ?? {};
}

export class RealtimeToolRegistry {
  private tools: Map<string, RealtimeToolDefinition>;

  constructor(definitions: RealtimeToolDefinition[]) {
    this.tools = new Map(definitions.map((tool) => [tool.name, tool]));
  }

  getFunctionDeclarations(options?: { provider?: 'google-ai-studio' | 'vertex-ai' }): Record<string, unknown>[] {
    const isVertexAI = options?.provider === 'vertex-ai';

    return Array.from(this.tools.values()).map((tool) => {
      // Build base parameters
      const baseParams = tool.parameters ?? {
        type: 'object',
        properties: {},
      };

      // Strip additionalProperties for Google AI Studio (not supported)
      // Keep it for Vertex AI if present
      const params = { ...baseParams } as Record<string, unknown>;
      if (!isVertexAI) {
        delete params.additionalProperties;
      }

      const decl: Record<string, unknown> = {
        name: tool.name,
        description: tool.description,
        parameters: params,
      };

      // Include behavior field for Google AI Studio (supports NON_BLOCKING)
      if (!isVertexAI && tool.behavior) {
        decl.behavior = tool.behavior;
      }

      return decl;
    });
  }

  async execute(call: RealtimeFunctionCall, context: RealtimeToolContext): Promise<RealtimeToolExecutionResult> {
    const tool = this.tools.get(call.name);
    if (!tool) {
      return {
        response: {
          error: `Unknown tool: ${call.name}`,
        },
      };
    }

    const args = normalizeArgs(call);
    return tool.handler(args, context);
  }
}

export function createDefaultRealtimeToolRegistry(
  context: RealtimeToolContext,
  extraDefinitions: RealtimeToolDefinition[] = []
): RealtimeToolRegistry {
  const definitions: RealtimeToolDefinition[] = [...extraDefinitions];

  return new RealtimeToolRegistry(definitions);
}

/**
 * Legacy tool definition kept for future rollback.
 * Currently not registered in the default tool list.
 */
export function createLegacyEndCallToolDefinition(context: RealtimeToolContext): RealtimeToolDefinition {
  return {
    name: 'end_call',
    description: 'End the live tutoring call. ONLY use this tool when the student EXPLICITLY says words like "end the call", "hang up", "goodbye", "stop the call", or "I want to leave". Do NOT call this tool automatically or for any other reason.',
    parameters: {
      type: 'object',
      properties: {
        user_request: {
          type: 'string',
          description: 'The EXACT words the student said to request ending the call. This field is REQUIRED - you must quote what the student actually said.',
        },
      },
      required: ['user_request'],
      additionalProperties: false,
    },
    handler: async (args: unknown) => {
      const params = args as { user_request?: string };
      const userRequest = params.user_request?.toLowerCase() || '';

      // Check if the user actually requested to end
      const endPhrases = ['end', 'hang up', 'bye', 'goodbye', 'stop', 'leave', 'quit', 'exit', 'done', 'finish'];
      const hasEndRequest = endPhrases.some(phrase => userRequest.includes(phrase));

      if (!userRequest || userRequest.length < 3) {
        return {
          response: {
            error: 'Cannot end call: You must provide the exact words the student said requesting to end the call.',
            instruction: 'Continue the tutoring session. Only end when the student explicitly asks to end or hang up.'
          },
        };
      }

      if (!hasEndRequest) {
        return {
          response: {
            error: `Cannot end call: "${userRequest}" does not appear to be a request to end the call.`,
            instruction: 'Continue the tutoring session. The student has not asked to end the call.'
          },
        };
      }

      return {
        response: { result: 'ending_call' },
        afterResponse: context.endSession,
      };
    },
  };
}
